import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, Search, RefreshCw, Users, ScrollText, Settings, LogOut, BarChart3,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/search", label: "Search candidates", icon: Search, testid: "nav-search" },
  { to: "/sync", label: "Sync Sheet", icon: RefreshCw, testid: "nav-sync" },
  { to: "/productivity", label: "Team Productivity", icon: BarChart3, roles: ["super_custodian", "hod", "tl"], testid: "nav-productivity" },
  { to: "/sync-log", label: "Sync Log", icon: ScrollText, testid: "nav-sync-log" },
  { to: "/users", label: "User Management", icon: Users, roles: ["super_custodian", "hod"], testid: "nav-users" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const ROLE_LABEL = {
  super_custodian: "Super Custodian",
  hod: "Head of Delivery",
  tl: "Team Lead",
  recruiter: "Recruiter",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV.filter((i) => !i.roles || i.roles.includes(user?.role));

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center text-white font-bold font-display" data-testid="brand-logo-mark">
              S
            </div>
            <div>
              <div className="font-display font-bold text-slate-900 text-lg leading-tight" data-testid="brand-name">Sourcd</div>
              <div className="text-[10px] tracking-[0.2em] font-semibold text-indigo-600 uppercase" data-testid="brand-tagline">By GetWork</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                  isActive
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <it.icon className="w-4 h-4" strokeWidth={2} />
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold text-slate-900 truncate" data-testid="sidebar-user-name">{user?.name || user?.email}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            <div className="text-[10px] tracking-wide font-semibold text-indigo-600 uppercase mt-1">
              {ROLE_LABEL[user?.role] || user?.role}
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-red-50 hover:text-red-700 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 animate-fade-up">{children}</div>
      </main>
    </div>
  );
}
